import { useCallback, useEffect, useRef, useState } from "react";
import { Headphones, Loader2, Pause, Play, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/text-to-speech`;
const SAMPLE_RATE = 24000;

/** Collect the readable prose of the current page, in document order. */
function collectPageText(): string[] {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>("h1, h2, h3, p, blockquote, li"),
  );
  const parts: string[] = [];

  for (const node of nodes) {
    if (node.closest("nav, footer, header, button, [data-no-tts]")) continue;
    if (node.getAttribute("aria-hidden") === "true") continue;
    if (node.offsetParent === null && node.getClientRects().length === 0) continue;
    if (node.querySelector("h1, h2, h3, p, blockquote, li")) continue;

    const text = (node.innerText || "").replace(/\s+/g, " ").trim();
    if (text.length < 2) continue;
    if (parts[parts.length - 1] === text) continue;
    parts.push(/[.!?:,]$/.test(text) ? text : `${text}.`);
  }

  return parts;
}

/** Group sentences into chunks that stay well under the model input limit. */
function chunkText(parts: string[], maxWords = 220): string[] {
  const words = (s: string) => (s.match(/\S+/g) ?? []).length;
  const chunks: string[] = [];
  let current = "";

  for (const part of parts) {
    if (words(part) > maxWords) {
      if (current.trim()) chunks.push(current.trim());
      current = "";
      const w = part.match(/\S+/g) ?? [];
      for (let i = 0; i < w.length; i += maxWords) {
        chunks.push(w.slice(i, i + maxWords).join(" "));
      }
      continue;
    }
    if (current && words(current) + words(part) > maxWords) {
      chunks.push(current.trim());
      current = "";
    }
    current += `${part} `;
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

type State = "idle" | "loading" | "playing" | "paused";

interface ListenButtonProps {
  /** Short label describing what is being read, e.g. "Read this page aloud". */
  label?: string;
  /**
   * Optional narration script. When provided, this spoken explanation is read
   * instead of scraping the visible page text.
   */
  script?: string;
  className?: string;
}

const ListenButton = ({ label = "Listen to this page", script, className }: ListenButtonProps) => {
  const [state, setState] = useState<State>("idle");
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const ctxRef = useRef<AudioContext | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const playheadRef = useRef(0);
  const sourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const cancelledRef = useRef(false);

  const teardown = useCallback(() => {
    cancelledRef.current = true;
    abortRef.current?.abort();
    abortRef.current = null;
    sourcesRef.current.forEach((s) => {
      try {
        s.stop();
      } catch {
        /* already stopped */
      }
    });
    sourcesRef.current = [];
    ctxRef.current?.close().catch(() => {});
    ctxRef.current = null;
    playheadRef.current = 0;
    setProgress({ current: 0, total: 0 });
    setState("idle");
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  const schedulePcm = (ctx: AudioContext, bytes: Uint8Array, pendingRef: { value: Uint8Array }) => {
    const merged = new Uint8Array(pendingRef.value.length + bytes.length);
    merged.set(pendingRef.value);
    merged.set(bytes, pendingRef.value.length);
    const usable = merged.length - (merged.length % 2);
    pendingRef.value = merged.slice(usable);
    if (usable === 0) return;

    const samples = new Int16Array(merged.buffer, 0, usable / 2);
    const floats = Float32Array.from(samples, (s) => s / 32768);
    const buffer = ctx.createBuffer(1, floats.length, SAMPLE_RATE);
    buffer.copyToChannel(floats, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    if (playheadRef.current === 0) {
      playheadRef.current = ctx.currentTime + 0.12;
    } else {
      playheadRef.current = Math.max(playheadRef.current, ctx.currentTime);
    }
    source.start(playheadRef.current);
    setState((prev) => (prev === "loading" ? "playing" : prev));
    playheadRef.current += buffer.duration;
    sourcesRef.current.push(source);
    source.onended = () => {
      sourcesRef.current = sourcesRef.current.filter((s) => s !== source);
    };
  };

  const streamChunk = async (ctx: AudioContext, text: string, signal: AbortSignal) => {
    const res = await fetch(FUNCTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ text }),
      signal,
    });

    if (!res.ok || !res.body) {
      throw new Error(`Speech request failed (${res.status})`);
    }

    const pending = { value: new Uint8Array(0) };
    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (!data || data === "[DONE]") continue;
        let payload: { type?: string; audio?: string };
        try {
          payload = JSON.parse(data);
        } catch {
          continue;
        }
        if (payload.type !== "speech.audio.delta" || !payload.audio) continue;
        const binary = atob(payload.audio);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        schedulePcm(ctx, bytes, pending);
      }
    }
  };

  const start = async () => {
    const source = script
      ? script
          .split(/\n+/)
          .map((line) => line.replace(/\s+/g, " ").trim())
          .filter(Boolean)
      : collectPageText();
    const chunks = chunkText(source);
    if (chunks.length === 0) {
      toast.error("Nothing to read on this page.");
      return;
    }

    cancelledRef.current = false;
    setState("loading");
    setProgress({ current: 0, total: chunks.length });

    const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
    ctxRef.current = ctx;
    if (ctx.state === "suspended") await ctx.resume().catch(() => {});

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      for (let i = 0; i < chunks.length; i++) {
        if (cancelledRef.current) return;
        setProgress({ current: i + 1, total: chunks.length });
        await streamChunk(ctx, chunks[i], controller.signal);
        // Stay a little ahead of playback without buffering the whole page.
        while (
          !cancelledRef.current &&
          playheadRef.current - ctx.currentTime > 25
        ) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }

      const remaining = Math.max(0, (playheadRef.current - ctx.currentTime) * 1000);
      window.setTimeout(() => {
        if (!cancelledRef.current) teardown();
      }, remaining + 300);
    } catch (err) {
      if (cancelledRef.current || controller.signal.aborted) return;
      console.error("Listen failed:", err);
      toast.error("Could not play the audio. Please try again.");
      teardown();
    }
  };

  const togglePause = async () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (state === "playing" || state === "loading") {
      await ctx.suspend().catch(() => {});
      setState("paused");
    } else {
      await ctx.resume().catch(() => {});
      setState("playing");
    }
  };

  const isActive = state !== "idle";

  return (
    <div
      data-no-tts
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-border bg-background/80 p-1 backdrop-blur-sm",
        className,
      )}
    >
      <button
        type="button"
        onClick={isActive ? togglePause : start}
        aria-label={
          state === "playing" ? "Pause reading this page aloud" : label
        }
        className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {state === "loading" ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : state === "playing" ? (
          <Pause className="h-4 w-4" aria-hidden="true" />
        ) : state === "paused" ? (
          <Play className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Headphones className="h-4 w-4" aria-hidden="true" />
        )}
        <span>
          {state === "idle"
            ? label
            : state === "loading" && progress.current <= 1
              ? "Preparing audio…"
              : state === "paused"
                ? "Resume"
                : `Reading ${progress.current} / ${progress.total}`}
        </span>
      </button>

      {isActive && (
        <button
          type="button"
          onClick={teardown}
          aria-label="Stop reading this page aloud"
          className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default ListenButton;
